import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import defaultCover from "../../assets/images/c1.png";
import { useAuth } from "../../context/AuthContext";
import { usePlayer, usePlayerProgress } from "../../context/PlayerContext";
import { hasCreatorAccess } from "../../utils/authRoles";
import "./navbar.scss";

const IconOrbit = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M20.3414 6.48389C21.4202 8.11454 21.9973 10.0257 22.0014 11.9809C22.0054 13.9361 21.4362 15.8496 20.3641 17.4847C19.292 19.1198 17.7641 20.4047 15.9694 21.1806C14.1747 21.9564 12.1919 22.1891 10.2664 21.8499M3.65931 17.5161C2.5805 15.885 2.00361 13.9734 2.00002 12.0179C1.99643 10.0623 2.5663 8.1486 3.63912 6.51357C4.71193 4.87854 6.2406 3.59395 8.03596 2.81875C9.83133 2.04356 11.8146 1.81179 13.7403 2.15211M15.0004 12C15.0004 13.6569 13.6572 15 12.0004 15C10.3435 15 9.00037 13.6569 9.00037 12C9.00037 10.3431 10.3435 9 12.0004 9C13.6572 9 15.0004 10.3431 15.0004 12ZM21.0004 5C21.0004 6.10457 20.1049 7 19.0004 7C17.8958 7 17.0004 6.10457 17.0004 5C17.0004 3.89543 17.8958 3 19.0004 3C20.1049 3 21.0004 3.89543 21.0004 5ZM7.00037 19C7.00037 20.1046 6.10494 21 5.00037 21C3.8958 21 3.00037 20.1046 3.00037 19C3.00037 17.8954 3.8958 17 5.00037 17C6.10494 17 7.00037 17.8954 7.00037 19Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconAnalytics = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H22M20 10L15 15L11 11L8 14" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconUpload = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M8 12H16M12 8V16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconVault = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M6.261 3H17.739C20.643 3 23 5.2632 23 8.0526V19.609C23 20.9286 21.884 22 20.51 22H3.49C2.116 22 1 20.9286 1 19.609V8.0526C1 5.2632 3.357 3 6.261 3Z" stroke="currentColor" strokeWidth="1.3" strokeMiterlimit="10"/>
    <path d="M11.997 9.2518C13.206 9.2518 14.189 10.1954 14.189 11.3571V14.8909C14.189 15.3646 13.79 15.7481 13.296 15.7481H10.701C10.208 15.7481 9.809 15.3646 9.809 14.8909V11.3571C9.809 10.1954 10.791 9.2518 12.001 9.2518H11.997Z" stroke="currentColor" strokeWidth="1.3" strokeMiterlimit="10"/>
    <path d="M1 12.5H9.808M14.191 12.5H22.999" stroke="currentColor" strokeWidth="1.3" strokeMiterlimit="10"/>
  </svg>
);

const IconChevron = ({ flip = false }) => (
  <svg
    className="navbar__chevron-vector"
    width="5"
    height="8"
    viewBox="0 0 5 8"
    fill="none"
    style={flip ? { transform: "scaleX(-1)" } : undefined}
    aria-hidden="true"
  >
    <path d="M0.648438 0.649902L3.98177 3.98324L0.648438 7.31657" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconExpand = () => (
  <span className="navbar__expand-icon" aria-hidden="true">
    <IconChevron flip />
    <IconChevron />
  </span>
);

const IconDockPlay = () => (
  <svg className="navbar__play-icon" xmlns="http://www.w3.org/2000/svg" width="41" height="39" viewBox="0 0 41 39" fill="none" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.0754 0C16.1048 0 12.2235 1.11989 8.9221 3.21805C5.62072 5.3162 3.04761 8.2984 1.52816 11.7875C0.00869844 15.2766 -0.388861 19.1159 0.385752 22.8199C1.16036 26.524 3.07236 29.9263 5.87995 32.5968C8.68754 35.2672 12.2646 37.0858 16.1589 37.8226C20.0531 38.5594 24.0896 38.1812 27.7579 36.736C31.4262 35.2908 34.5615 32.8433 36.7674 29.7032C38.9733 26.5631 40.1507 22.8713 40.1507 19.0947C40.1507 16.5872 39.6315 14.1042 38.6226 11.7875C37.6137 9.47082 36.135 7.36583 34.2708 5.59272C32.4066 3.81961 30.1935 2.4131 27.7579 1.4535C25.3222 0.4939 22.7117 0 20.0754 0ZM16.1589 25.3848V11.7992L27.1608 19.0947L16.1589 25.3848Z"
      fill="white"
    />
  </svg>
);

const IconDockPause = () => (
  <svg className="navbar__play-icon" xmlns="http://www.w3.org/2000/svg" width="41" height="39" viewBox="0 0 41 39" fill="none" aria-hidden="true">
    <path d="M20.0754 0C16.1048 0 12.2235 1.11989 8.9221 3.21805C5.62072 5.3162 3.04761 8.2984 1.52816 11.7875C0.00869844 15.2766 -0.388861 19.1159 0.385752 22.8199C1.16036 26.524 3.07236 29.9263 5.87995 32.5968C8.68754 35.2672 12.2646 37.0858 16.1589 37.8226C20.0531 38.5594 24.0896 38.1812 27.7579 36.736C31.4262 35.2908 34.5615 32.8433 36.7674 29.7032C38.9733 26.5631 40.1507 22.8713 40.1507 19.0947C40.1507 16.5872 39.6315 14.1042 38.6226 11.7875C37.6137 9.47082 36.135 7.36583 34.2708 5.59272C32.4066 3.81961 30.1935 2.4131 27.7579 1.4535C25.3222 0.4939 22.7117 0 20.0754 0Z" fill="white" />
    <path d="M16.75 13.25V24.95M23.35 13.25V24.95" stroke="#14150D" strokeWidth="2.15" strokeLinecap="round" />
  </svg>
);

const NAV_ITEMS = [
  { to: "/lobby", label: "Home", icon: <IconOrbit /> },
  { to: "/dashboard", label: "Dashboard", creatorOnly: true, icon: <IconAnalytics /> },
  { to: "/upload", label: "Upload", creatorOnly: true, icon: <IconUpload /> },
  { to: "/vault", label: "Vault", icon: <IconVault /> },
];

const FALLBACK_DOCK_TRACK = {
  id: "1",
  title: "No Matter What",
  artist: "Jax Dope",
  artwork: defaultCover,
  duration: "3:30",
};

const ASK_VISTA_PILL_ART = `${process.env.PUBLIC_URL || ""}/figma/ask-vista-orb.svg`;

const MOBILE_QUERY = "(max-width: 1439px)";

function getIsMobileViewport() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia(MOBILE_QUERY).matches;
}

function getInitialPlayerExpanded({ isLobby = false, isPlayerRoute = false } = {}) {
  const isMobile = getIsMobileViewport();

  if (isMobile) {
    return isPlayerRoute;
  }

  return true;
}

const Navbar = ({ isLobby = false, isNotifications = false, isVistaOpen = false, onVistaToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTrack, play, togglePlay, skipNext } = usePlayer();
  const { isPlaying } = usePlayerProgress();
  const isPlayerRoute = location.pathname.startsWith("/player/");
  const [isMobileViewport, setIsMobileViewport] = useState(getIsMobileViewport);
  const [isPlayerExpanded, setIsPlayerExpanded] = useState(() =>
    getInitialPlayerExpanded({ isLobby, isPlayerRoute })
  );
  const isCreator = hasCreatorAccess(user);
  const isMobilePlaybackDock = isMobileViewport && isPlayerExpanded;
  const useCreatorDock = isCreator;
  const navItems = useMemo(() => {
    const roleItems = NAV_ITEMS.filter((item) => !(item.creatorOnly && !useCreatorDock));
    const mobileRoleItems = useCreatorDock
      ? roleItems
      : roleItems.filter((item) => ["/lobby", "/vault"].includes(item.to));

    if (isMobileViewport) {
      return isMobilePlaybackDock ? [] : mobileRoleItems;
    }

    if (isPlayerExpanded || useCreatorDock) {
      return roleItems;
    }

    return roleItems.filter((item) => ["/lobby", "/vault"].includes(item.to));
  }, [isMobilePlaybackDock, isMobileViewport, isPlayerExpanded, useCreatorDock]);
  const displayTrack = currentTrack || FALLBACK_DOCK_TRACK;
  const shouldRenderSep = navItems.length > 0 || isMobilePlaybackDock;

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const syncViewport = () => setIsMobileViewport(mediaQuery.matches);
    syncViewport();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", syncViewport);
      return () => mediaQuery.removeEventListener("change", syncViewport);
    }

    if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(syncViewport);
      return () => mediaQuery.removeListener(syncViewport);
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (isMobileViewport) {
      setIsPlayerExpanded(isPlayerRoute);
      return;
    }

    setIsPlayerExpanded(true);
  }, [isLobby, isMobileViewport, isPlayerRoute]);

  useEffect(() => {
    if (isNotifications && !isMobileViewport) {
      setIsPlayerExpanded(true);
    }
  }, [isMobileViewport, isNotifications]);

  const renderVistaPill = (variantClassName = "") => (
    <button
      className={`vista-pill${variantClassName ? ` ${variantClassName}` : ""}`}
      type="button"
      aria-label="Ask Vista"
      onClick={() => onVistaToggle && onVistaToggle()}
    >
      <img src={ASK_VISTA_PILL_ART} alt="" className="vista-pill__art vista-pill__art--pill" aria-hidden="true" />
    </button>
  );

  const handlePlayToggle = () => {
    if (!currentTrack) {
      play(displayTrack, [displayTrack]);
      return;
    }

    togglePlay();
  };

  const handleNext = () => {
    if (!currentTrack) {
      navigate(`/player/${displayTrack.id}`);
      return;
    }

    skipNext();
  };

  const renderPlayerToggle = () => (
    <button
      className="navbar__status navbar__player-collapsed"
      type="button"
      aria-label={isPlayerExpanded ? "Collapse player controls" : "Expand player controls"}
      aria-expanded={isPlayerExpanded}
      onClick={() => setIsPlayerExpanded((expanded) => !expanded)}
    >
      <IconExpand />
    </button>
  );

  return (
    <>
      <div
        className={`navbar-dock${isLobby ? " navbar-dock--lobby" : ""}${isNotifications ? " navbar-dock--notifications" : ""}${isPlayerRoute ? " navbar-dock--player" : ""}${isVistaOpen ? " navbar-dock--vista-open" : ""}`}
      >
          <nav
            className={`navbar navbar--with-player navbar--${useCreatorDock ? "creator" : "consumer"}${isPlayerExpanded ? " navbar--expanded" : " navbar--collapsed"}${isMobileViewport ? " navbar--mobile" : ""}${isMobilePlaybackDock ? " navbar--mobile-playback" : ""}${isNotifications ? " navbar--notifications" : ""}${isPlayerRoute ? " navbar--player" : ""}`}
            aria-label="Primary navigation"
          >
            {navItems.length > 0 ? (
              <div className="navbar__buttons">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `navbar__btn navbar__btn--${item.to.replace("/", "") || "home"}${isActive ? " navbar__btn--active" : ""}`
                    }
                    aria-label={item.label}
                  >
                    <span className="navbar__btn-inner">
                      {item.icon}
                    </span>
                  </NavLink>
                ))}
              </div>
            ) : null}

            {shouldRenderSep ? <div className="navbar__sep" /> : null}
            <div
              className={`navbar__player${isPlayerExpanded ? " navbar__player--expanded" : " navbar__player--collapsed navbar__player-collapsed"}`}
            >
              {renderPlayerToggle()}

              {isPlayerExpanded ? (
                <>
                  <button
                    className="navbar__track-link"
                    type="button"
                    aria-label={`Open ${displayTrack.title} by ${displayTrack.artist}`}
                    onClick={() => navigate(`/player/${displayTrack.id}`)}
                  >
                    <span className="navbar__cover">
                      {displayTrack.artwork ? (
                        <img src={displayTrack.artwork} alt="" />
                      ) : (
                        <span className="navbar__cover-placeholder" />
                      )}
                    </span>
                    <span className="navbar__track-copy">
                      <span className="navbar__track-title">{displayTrack.title}</span>
                      <span className="navbar__track-artist">{displayTrack.artist}</span>
                    </span>
                  </button>

                  <div className="navbar__transport">
                    <button
                      className="navbar__play"
                      type="button"
                      aria-label={currentTrack && isPlaying ? "Pause" : "Play"}
                      onClick={handlePlayToggle}
                    >
                      {currentTrack && isPlaying ? (
                        <IconDockPause />
                      ) : (
                        <IconDockPlay />
                      )}
                    </button>

                    <button className="navbar__skip" type="button" aria-label="Dock skip forward" onClick={handleNext}>
                      <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <path d="M17.5 3.33325V16.6666" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M5.02417 3.57077C4.77126 3.41902 4.48261 3.33711 4.18769 3.33338C3.89278 3.32966 3.60216 3.40427 3.3455 3.54959C3.08884 3.69491 2.87534 3.90573 2.7268 4.16053C2.57826 4.41534 2.5 4.70499 2.5 4.99994V14.9999C2.5 15.2949 2.57826 15.5845 2.7268 15.8393C2.87534 16.0941 3.08884 16.305 3.3455 16.4503C3.60216 16.5956 3.89278 16.6702 4.18769 16.6665C4.48261 16.6628 4.77126 16.5808 5.02417 16.4291L13.355 11.4308C13.6023 11.283 13.8071 11.0737 13.9494 10.8232C14.0917 10.5727 14.1666 10.2896 14.1668 10.0015C14.1671 9.71339 14.0927 9.43016 13.9508 9.17941C13.809 8.92866 13.6045 8.71896 13.3575 8.57077L5.02417 3.57077Z" fill="currentColor"/>
                      </svg>
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </nav>
        </div>

      {renderVistaPill(
        isPlayerRoute
          ? "vista-pill--player"
          : isNotifications
            ? "vista-pill--notifications"
            : isLobby
              ? "vista-pill--lobby"
              : ""
      )}
    </>
  );
};

export default Navbar;
